/** Right-anchored review drawer for the batch grid's pending edits, grouped
 *  per tenant. Every row is individually droppable, tenants drop wholesale,
 *  and the footer carries undo/redo, reset-all (confirmed) and the persist
 *  entry point. Secret values never render in clear text here. */
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RedoIcon from '@mui/icons-material/Redo';
import UndoIcon from '@mui/icons-material/Undo';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useMemo, useState } from 'react';

import { MASKED_SECRET } from '@/api/tourOperatorConfigs';
import { ConfirmDialog } from '@/components/ConfirmDialog';

import { safePreview } from '../config/schema-grid/schemaOps';

import { useBatchData } from './BatchDataContext';
import { UNSET, type PendingEdit } from './changeSet';
import { useCanUndoRedo, useChangeSet } from './ChangeSetContext';
import { canonicalize } from './pathOps';

interface TenantGroup {
  tenantId: number;
  name: string;
  edits: Array<{ path: string; edit: PendingEdit }>;
}

function ValuePreview({ value, secret }: { value: unknown | typeof UNSET; secret: boolean }) {
  if (value === UNSET) {
    return (
      <Box component="span" sx={{ color: 'error.main', textDecoration: 'line-through' }}>
        removed
      </Box>
    );
  }
  return <Box component="span">{secret ? MASKED_SECRET : safePreview(value, 60)}</Box>;
}

export function PendingChangesDrawer({
  open,
  onClose,
  onPersist,
  secretKeys,
}: {
  open: boolean;
  onClose: () => void;
  /** Opens the persist dialog (the page owns it). */
  onPersist: () => void;
  /** Canonical keys whose values must render masked. */
  secretKeys: Set<string>;
}) {
  const { state, dispatch } = useChangeSet();
  const { tenantById } = useBatchData();
  const { canUndo, canRedo } = useCanUndoRedo();
  const [confirmReset, setConfirmReset] = useState(false);

  const groups = useMemo<TenantGroup[]>(() => {
    const out: TenantGroup[] = [];
    for (const [tidStr, tenantEdits] of Object.entries(state.edits)) {
      const tenantId = Number(tidStr);
      const edits = Object.entries(tenantEdits)
        .map(([path, edit]) => ({ path, edit }))
        .sort((a, b) => a.path.localeCompare(b.path));
      if (edits.length === 0) continue;
      out.push({ tenantId, name: tenantById.get(tenantId)?.name ?? `#${tenantId}`, edits });
    }
    return out.sort((a, b) => a.name.localeCompare(b.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.version, tenantById]);

  const total = groups.reduce((n, g) => n + g.edits.length, 0);

  const isSecret = (path: string): boolean => {
    try {
      return secretKeys.has(canonicalize(path));
    } catch {
      return false;
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      // The AppBar sits at zIndex.drawer+1; temporary drawers default to
      // zIndex.drawer, so without this the app bar covers the drawer top.
      sx={{ zIndex: (t) => t.zIndex.modal }}
    >
      <Box sx={{ width: 480, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>
            Pending changes
          </Typography>
          <Chip label={total} size="small" color={total > 0 ? 'warning' : 'default'} sx={{ mr: 1 }} />
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Divider />

        <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5 }}>
          {groups.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              No pending edits. Expand a key in the grid and use “Edit values”.
            </Typography>
          )}
          {groups.map((group) => (
            <Accordion key={group.tenantId} defaultExpanded={groups.length <= 5} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" noWrap sx={{ flex: 1 }}>
                    {group.name}
                  </Typography>
                  <Chip label={group.edits.length} size="small" />
                  <Tooltip title="Drop all edits for this tenant">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        dispatch({ type: 'DROP_TENANT', tenantId: group.tenantId });
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                {group.edits.map(({ path, edit }) => {
                  const secret = isSecret(path);
                  return (
                    <Box
                      key={path}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.25 }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="caption"
                          noWrap
                          title={path}
                          sx={{ fontFamily: 'monospace', display: 'block' }}
                        >
                          {path}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" component="div">
                          <ValuePreview value={edit.before} secret={secret} />
                          {' → '}
                          <ValuePreview value={edit.after} secret={secret} />
                        </Typography>
                      </Box>
                      {edit.stale && (
                        <Tooltip title="The server value changed since you loaded — resolve in the persist dialog.">
                          <WarningAmberIcon fontSize="small" color="warning" />
                        </Tooltip>
                      )}
                      <Tooltip title="Drop this edit">
                        <IconButton
                          size="small"
                          onClick={() =>
                            dispatch({ type: 'REMOVE_EDIT', tenantId: group.tenantId, path })
                          }
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  );
                })}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        <Divider />
        <Stack direction="row" spacing={1} sx={{ p: 1.5, alignItems: 'center' }}>
          <Tooltip title="Undo (Ctrl/Cmd+Z)">
            <span>
              <IconButton size="small" disabled={!canUndo} onClick={() => dispatch({ type: 'UNDO' })}>
                <UndoIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Redo (Ctrl/Cmd+Shift+Z)">
            <span>
              <IconButton size="small" disabled={!canRedo} onClick={() => dispatch({ type: 'REDO' })}>
                <RedoIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Button size="small" color="error" disabled={total === 0} onClick={() => setConfirmReset(true)}>
            Reset all
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button variant="contained" size="small" disabled={total === 0} onClick={onPersist}>
            Persist changes…
          </Button>
        </Stack>
      </Box>

      <ConfirmDialog
        open={confirmReset}
        title="Reset all pending changes?"
        message={`Drop all ${total} pending edit${total === 1 ? '' : 's'}? Nothing has been saved to any tenant; Ctrl/Cmd+Z can still bring them back while the page stays open.`}
        confirmLabel="Reset all"
        destructive
        onConfirm={() => {
          dispatch({ type: 'RESET_ALL' });
          setConfirmReset(false);
        }}
        onCancel={() => setConfirmReset(false)}
      />
    </Drawer>
  );
}
